<template>
  <div class="medical-record-card" v-for="(medicalRecord, index) in medicalRecords" :key="medicalRecord.id_medrec">
    <div class="card-header-modern collapsible cursor-pointer rotate"
         :class="isRecordOpen(medicalRecord.id_medrec, index) ? 'active' : 'collapsed'"
         @click="toggleRecord(medicalRecord.id_medrec, index, $event)">
      <div class="card-title">
        <div class="record-type-icon">
          <span class="svg-icon svg-icon-2 svg-icon-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
              <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
            </svg>
          </span>
        </div>
        <h2 class="record-title">{{ attendanceRecordTitle(medicalRecord) }}</h2>
      </div>
      <div class="d-flex align-items-center gap-2 ms-auto me-3 record-header-actions" @click.stop>
        <RecordAttributesManager :medical-record="medicalRecord" :schedule="schedule"/>
        <button
          type="button"
          class="btn btn-icon btn-sm btn-light-primary"
          title="Imprimir prontuário"
          :disabled="printingMedrecId === medicalRecord.id_medrec"
          @click.stop.prevent="printMedicalRecord(medicalRecord)">
          <i class="bi bi-printer"></i>
        </button>
        <button
          v-if="canDeleteRecord"
          type="button"
          class="btn btn-icon btn-sm btn-light-danger"
          title="Remover registro deste atendimento"
          :disabled="removingRecordId === medicalRecord.id_medrec"
          @click.stop.prevent="removeMedicalRecord(medicalRecord)">
          <i class="bi bi-trash"></i>
        </button>
      </div>
      <div class="card-toolbar rotate-180">
        <span class="svg-icon svg-icon-1 svg-icon-white">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)"
                  fill="currentColor"></rect>
            <path
                d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z"
                fill="currentColor"></path>
          </svg>
        </span>
      </div>
    </div>
    <div :id="'kt_docs_card_collapsible_' + medicalRecord.id_medrec" class="collapse"
         :class="{ show: isRecordOpen(medicalRecord.id_medrec, index) }">
      <div class="card-body" :class="{ 'att-record-clinical-wrap': isClinical }">
        <div v-if="isClinical" class="att-record-clinical">
          <nav class="att-record-rail" aria-label="Funções do prontuário">
            <button
              v-for="tab in clinicalRecordTabs(medicalRecord)"
              :key="`clinical-${medicalRecord.id_medrec}-${tab.id}`"
              type="button"
              class="att-record-rail-btn"
              :class="{ 'is-active': isTabActive(medicalRecord.id_medrec, tab.id) }"
              @click="setActiveTab(medicalRecord.id_medrec, tab.id)"
            >
              <i :class="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </button>
          </nav>

          <div class="att-record-clinical-main">
            <AttendanceRecordForm
              v-show="isTabActive(medicalRecord.id_medrec, 'prontuario')"
              :medical-record="medicalRecord"
              :schedule="schedule"
              :record-types="recordTypes"
              @onChange="saveAttribute"
            />

            <div v-show="isClinicalToolTab(medicalRecord)" class="content-card">
              <div class="card-body" style="padding-top: 0.5rem; padding-bottom: 0.5rem;">
                <div class="tab-content">
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'pre-schedule') }]"
                       :id="'tab-clinical-pre-schedule-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="hasPreScheduleAnswer(medicalRecord)">
                    <PreScheduleAnswerView :answer="medicalRecord.schedule.pre_schedule_answer">
                      <template #question-actions="{ question, formattedAnswer }">
                        <button
                          v-if="question.question_type === 'file'"
                          class="btn btn-sm btn-icon btn-light-primary"
                          :disabled="addingAttachmentKey === attachmentActionKey(medicalRecord, question)"
                          @click="addFileToAttachments(medicalRecord, question)"
                          title="Adicionar aos anexos da consulta">
                          <span v-if="addingAttachmentKey === attachmentActionKey(medicalRecord, question)" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="bi bi-paperclip"></i>
                        </button>
                        <button
                          v-else
                          class="btn btn-sm btn-icon btn-light-primary"
                          @click="copyToAttribute(medicalRecord, question, formattedAnswer)"
                          title="Copiar para atributo">
                          <i class="bi bi-clipboard-plus"></i>
                        </button>
                      </template>
                    </PreScheduleAnswerView>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'prescription') }]"
                       :id="'tab-clinical-prescription-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="medicalRecord.record_type.allow_prescription">
                    <Prescription :medical-record="medicalRecord" :schedule="schedule" :presc-types="prescTypes"
                                  :prescription-protocols="prescriptionProtocols" :drug-admins="drugsAdmins"
                                  :certifiers="certifiers" :record-types="recordTypes"
                                  :professional-id="schedule?.id_prof"
                                  :key="'clinical-presc-' + medicalRecord?.id_medrec"
                                  @open-ai-assistant="$emit('open-ai-assistant', $event)"
                                  @edit-patient-contact="$emit('edit-patient-contact')"
                                  @onModelSaved="$emit('onModelSaved')"/>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'exam') }]"
                       :id="'tab-clinical-exam-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="medicalRecord.record_type.allow_exam_request">
                    <ExamRequest :medical-record="medicalRecord" :schedule="schedule" :exam-protocols="examProtocols"
                                 :certifiers="certifiers" :key="'clinical-exam-' + medicalRecord?.id_medrec + '-' + aiFillNonce"
                                 @edit-patient-contact="$emit('edit-patient-contact')"
                                 @onModelSaved="$emit('onModelSaved')"/>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'programs') }]"
                       :id="'tab-clinical-programs-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="medicalRecord.record_type.allow_programs && isTabActive(medicalRecord.id_medrec, 'programs')">
                    <Programs
                      :medical-record="medicalRecord"
                      :schedule="schedule"
                      :key="'clinical-programs-' + medicalRecord?.id_medrec"/>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'document') }]"
                       :id="'tab-clinical-document-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="medicalRecord.record_type.allow_document">
                    <Document :medical-record="medicalRecord" :schedule="schedule"
                              :document-templates="documentTemplates"
                              :certifiers="certifiers" :key="'clinical-doc-' + medicalRecord?.id_medrec"
                              @edit-patient-contact="$emit('edit-patient-contact')"
                              @onModelSaved="$emit('onModelSaved')"/>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'health-data') }]"
                       :id="'tab-clinical-health-data-' + medicalRecord.id_medrec" role="tabpanel"
                       v-if="isTabActive(medicalRecord.id_medrec, 'health-data')">
                    <HealthData :medical-record="medicalRecord" :schedule="schedule"
                                :health-indicator-groups="healthIndicatorGroups" :auto-load="true"
                                :readonly="false" :key="'clinical-health-' + medicalRecord?.id_medrec + '-' + aiFillNonce"/>
                  </div>
                  <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'attachment') }]"
                       :id="'tab-clinical-attachment-' + medicalRecord.id_medrec" role="tabpanel">
                    <Attachment :medical-record="medicalRecord" :schedule="schedule" :key="'clinical-att-' + medicalRecord?.id_medrec"/>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-show="isTabActive(medicalRecord.id_medrec, 'cid')"
              v-if="medicalRecord.record_type.allow_document"
              class="cid-card"
            >
              <div class="cid-header">
                <div class="cid-title">
                  <span class="cid-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
                      <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <h3 class="cid-title-text">CID</h3>
                </div>
              </div>
              <div class="cid-body">
                <Cid :medical-record="medicalRecord" :schedule="schedule" :key="'clinical-cid-' + medicalRecord?.id_medrec + '-' + aiFillNonce"/>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <AttendanceRecordForm
            parts="band"
            :medical-record="medicalRecord"
            :schedule="schedule"
            :record-types="recordTypes"
            @onChange="saveAttribute"
          />

          <Splitter>
            <SplitterPanel :size="40">
              <AttendanceRecordForm
                parts="column"
                inset
                :medical-record="medicalRecord"
                :schedule="schedule"
                :record-types="recordTypes"
                @onChange="saveAttribute"
              />
            </SplitterPanel>

            <SplitterPanel v-if="medicalRecord.record_type.allow_prescription || medicalRecord.record_type.allow_exam_request || medicalRecord.record_type.allow_document || medicalRecord.record_type.allow_programs" :size="60">
              <div style="padding-left: 12px;">
                <div class="content-card">
                  <div class="modern-nav-header">
                  <!-- Navegação para telas maiores -->
                  <div class="nav-desktop-group">
                    <ul class="nav nav-modern">
                      <li
                        v-for="tab in primarySideTabs(medicalRecord)"
                        :key="`desktop-primary-${medicalRecord.id_medrec}-${tab.id}`"
                        class="nav-item"
                      >
                        <button
                            :class="['nav-btn', tab.btnClass, { 'active': isTabActive(medicalRecord.id_medrec, tab.id) }]"
                            data-bs-toggle="tab"
                            :href="'#tab-' + tab.id + '-' + medicalRecord.id_medrec"
                            @click="setActiveTab(medicalRecord.id_medrec, tab.id)"
                            :id="'nav-' + tab.id + '-' + medicalRecord.id_medrec">
                          <span class="nav-icon">
                            <i :class="tab.icon"></i>
                          </span>
                          <span class="nav-text">{{ tab.label }}</span>
                        </button>
                      </li>
                    </ul>
                    <div class="dropdown modern-dropdown" v-if="overflowSideTabs(medicalRecord).length">
                      <button
                        class="dropdown-btn"
                        type="button"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="true"
                        aria-expanded="false"
                        :class="{ 'is-current': isOverflowTabActive(medicalRecord) }"
                      >
                        <span class="dropdown-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="5" cy="12" r="2" fill="currentColor"/>
                            <circle cx="12" cy="12" r="2" fill="currentColor"/>
                            <circle cx="19" cy="12" r="2" fill="currentColor"/>
                          </svg>
                        </span>
                      </button>
                      <ul class="dropdown-menu modern-dropdown-menu">
                        <li v-for="tab in overflowSideTabs(medicalRecord)" :key="`desktop-overflow-${medicalRecord.id_medrec}-${tab.id}`">
                          <button
                            class="dropdown-item modern-dropdown-item"
                            :class="{ active: isTabActive(medicalRecord.id_medrec, tab.id) }"
                            @click="selectOverflowTab($event, medicalRecord, tab)"
                          >
                            <span class="dropdown-item-icon">
                              <i :class="tab.icon"></i>
                            </span>
                            <span class="dropdown-item-text">{{ tab.label }}</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Navegação para telas menores -->
                  <div class="nav-mobile">
                    <ul class="nav nav-modern-mobile">
                      <li
                        v-for="tab in primarySideTabs(medicalRecord)"
                        :key="`mobile-primary-${medicalRecord.id_medrec}-${tab.id}`"
                        class="nav-item"
                      >
                        <button
                            :class="['nav-btn-mobile', tab.btnClass, { 'active': isTabActive(medicalRecord.id_medrec, tab.id) }]"
                            data-bs-toggle="tab"
                            :href="'#tab-' + tab.id + '-' + medicalRecord.id_medrec"
                            @click="setActiveTab(medicalRecord.id_medrec, tab.id)"
                            :id="'nav-mobile-' + tab.id + '-' + medicalRecord.id_medrec">
                          <span class="nav-icon-mobile">
                            <i :class="tab.icon"></i>
                          </span>
                          <span class="nav-text-mobile">{{ tab.label }}</span>
                        </button>
                      </li>
                    </ul>

                    <div class="dropdown modern-dropdown" v-if="overflowSideTabs(medicalRecord).length">
                      <button
                        class="dropdown-btn"
                        style="margin-left: 10px;"
                        type="button"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="true"
                        aria-expanded="false"
                        :class="{ 'is-current': isOverflowTabActive(medicalRecord) }"
                      >
                        <span class="dropdown-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="5" cy="12" r="2" fill="currentColor"/>
                            <circle cx="12" cy="12" r="2" fill="currentColor"/>
                            <circle cx="19" cy="12" r="2" fill="currentColor"/>
                          </svg>
                        </span>
                      </button>
                      <ul class="dropdown-menu modern-dropdown-menu">
                        <li v-for="tab in overflowSideTabs(medicalRecord)" :key="`mobile-overflow-${medicalRecord.id_medrec}-${tab.id}`">
                          <button
                            class="dropdown-item modern-dropdown-item"
                            :class="{ active: isTabActive(medicalRecord.id_medrec, tab.id) }"
                            @click="selectOverflowTab($event, medicalRecord, tab)"
                          >
                            <span class="dropdown-item-icon">
                              <i :class="tab.icon"></i>
                            </span>
                            <span class="dropdown-item-text">{{ tab.label }}</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="card-body"
                     style="padding-top: 0.5rem; padding-bottom: 0.5rem;">
                  <div class="tab-content">
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'pre-schedule') }]" 
                         :id="'tab-pre-schedule-' + medicalRecord.id_medrec" role="tabpanel" v-if="hasPreScheduleAnswer(medicalRecord)">
                      <PreScheduleAnswerView :answer="medicalRecord.schedule.pre_schedule_answer">
                        <template #question-actions="{ question, formattedAnswer }">
                          <button
                            v-if="question.question_type === 'file'"
                            class="btn btn-sm btn-icon btn-light-primary"
                            :disabled="addingAttachmentKey === attachmentActionKey(medicalRecord, question)"
                            @click="addFileToAttachments(medicalRecord, question)"
                            title="Adicionar aos anexos da consulta">
                            <span v-if="addingAttachmentKey === attachmentActionKey(medicalRecord, question)" class="spinner-border spinner-border-sm"></span>
                            <i v-else class="bi bi-paperclip"></i>
                          </button>
                          <button
                            v-else
                            class="btn btn-sm btn-icon btn-light-primary"
                            @click="copyToAttribute(medicalRecord, question, formattedAnswer)"
                            title="Copiar para atributo">
                            <i class="bi bi-clipboard-plus"></i>
                          </button>
                        </template>
                      </PreScheduleAnswerView>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'prescription') }]" 
                         :id="'tab-prescription-' + medicalRecord.id_medrec" role="tabpanel"
                         v-if="medicalRecord.record_type.allow_prescription">
                      <Prescription :medical-record="medicalRecord" :schedule="schedule" :presc-types="prescTypes"
                                    :prescription-protocols="prescriptionProtocols" :drug-admins="drugsAdmins"
                                    :certifiers="certifiers" :record-types="recordTypes"
                                    :professional-id="schedule?.id_prof"
                                    :key="medicalRecord?.id_medrec"
                                    @open-ai-assistant="$emit('open-ai-assistant', $event)"
                                    @edit-patient-contact="$emit('edit-patient-contact')"
                                    @onModelSaved="$emit('onModelSaved')"/>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'exam') }]" 
                         :id="'tab-exam-' + medicalRecord.id_medrec" role="tabpanel"
                         v-if="medicalRecord.record_type.allow_exam_request">
                      <ExamRequest :medical-record="medicalRecord" :schedule="schedule" :exam-protocols="examProtocols"
                                   :certifiers="certifiers" :key="'exam-' + medicalRecord?.id_medrec + '-' + aiFillNonce"
                                   @edit-patient-contact="$emit('edit-patient-contact')"
                                   @onModelSaved="$emit('onModelSaved')"/>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'programs') }]"
                         :id="'tab-programs-' + medicalRecord.id_medrec" role="tabpanel"
                         v-if="medicalRecord.record_type.allow_programs && isTabActive(medicalRecord.id_medrec, 'programs')">
                      <Programs
                        :medical-record="medicalRecord"
                        :schedule="schedule"
                        :key="'programs-' + medicalRecord?.id_medrec"/>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'document') }]" 
                         :id="'tab-document-' + medicalRecord.id_medrec" role="tabpanel"
                         v-if="medicalRecord.record_type.allow_document">
                      <Document :medical-record="medicalRecord" :schedule="schedule"
                                :document-templates="documentTemplates"
                                :certifiers="certifiers" :key="medicalRecord?.id_medrec"
                                @edit-patient-contact="$emit('edit-patient-contact')"
                                @onModelSaved="$emit('onModelSaved')"/>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'health-data') }]" 
                         :id="'tab-health-data-' + medicalRecord.id_medrec" role="tabpanel"
                         v-if="isTabActive(medicalRecord.id_medrec, 'health-data')">
                      <HealthData :medical-record="medicalRecord" :schedule="schedule"
                                  :health-indicator-groups="healthIndicatorGroups" :auto-load="true"
                                  :readonly="false" :key="'health-' + medicalRecord?.id_medrec + '-' + aiFillNonce" ref="healthDataRef"/>
                    </div>
                    <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'attachment') }]" 
                         :id="'tab-attachment-' + medicalRecord.id_medrec" role="tabpanel">
                      <Attachment :medical-record="medicalRecord" :schedule="schedule" :key="medicalRecord?.id_medrec" ref="attachmentRef"/>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="medicalRecord.record_type.allow_document" class="cid-card">
                <div class="cid-header">
                  <div class="cid-title">
                    <span class="cid-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
                        <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <h3 class="cid-title-text">CID</h3>
                  </div>
                </div>
                <div class="cid-body">
                  <Cid :medical-record="medicalRecord" :schedule="schedule" :key="'cid-' + medicalRecord?.id_medrec + '-' + aiFillNonce"/>
                </div>
              </div>
            </div>
          </SplitterPanel>


        </Splitter>
        </template>
      </div>
    </div>
  </div>

  <PdfViewer id="modal_pdf_viewer_medical_record_attendance"
             :pdf-source="pdfSource"
             :file-name="pdfFileName"
             :signed="pdfSigned"/>

  <MedicalRecordPrinter
    ref="medicalRecordPrinter"
    id="attendance"
    :schedule="schedule"
    :certifiers="certifiers"
    @printed="onMedicalRecordPrinted"/>
</template>

<script>
import http from "../../../helpers/http";
import moment from "../../../helpers/date";
import InputAttendance from '../shared/Input';
import Alert from "../../../helpers/alert";
import permission from "../../../helpers/permission";
import Prescription from "./Prescription";
import ExamRequest from "./ExamRequest";
import Document from "./Document";
import Cid from "./Cid";
import HealthData from "./HealthData";
import Attachment from "./Attachment";
import Programs from "./Programs";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import OphthalmologyPanel from "../shared/OphthalmologyPanel.vue";
import OdontologyPanel from "../shared/OdontologyPanel.vue";
import PreScheduleAnswerView from "../../pre_schedule/PreScheduleAnswerView.vue";
import AttendanceRecordForm from "../shared/AttendanceRecordForm.vue";
import RecordAttributesManager from "../shared/RecordAttributesManager.vue";
import PdfViewer from "../shared/PdfViewer";
import MedicalRecordPrinter from "../shared/MedicalRecordPrinter.vue";
import {showMedicalRecordPdf} from "../shared/printMedicalRecord";
import {resolveSpecialtyPanel, splitFullWidthAttributes, PANEL_ODONTOLOGY, PANEL_OPHTHALMOLOGY} from "../shared/specialty-panel";
import {medicalRecordHeaderTitle} from "../shared/recordHeader";
import {visibleAttributes} from "../shared/record-attributes";
import {
  availableSideTabs as listSideTabs,
  clinicalRecordTabs as listClinicalTabs,
  defaultSideTab,
  hasPreScheduleAnswer as recordHasPreScheduleAnswer,
  isSideTabAllowed,
} from '@/helpers/attendanceSideTabs';

export default {
  name: "AttendanceTab",
  emits: ['open-ai-assistant', 'record-removed', 'onModelSaved', 'edit-patient-contact'],
  components: {
    AttendanceRecordForm,
    Document,
    InputAttendance,
    Prescription,
    ExamRequest,
    Cid,
    HealthData,
    Attachment,
    Splitter,
    SplitterPanel,
    OphthalmologyPanel,
    OdontologyPanel,
    PreScheduleAnswerView,
    Programs,
    RecordAttributesManager,
    PdfViewer,
    MedicalRecordPrinter,
  },
  props: {
    medicalRecords: Object,
    schedule: Object,
    recordTypes: Array,
    examProtocols: Array,
    prescriptionProtocols: Array,
    prescTypes: Array,
    drugsAdmins: Array,
    documentTemplates: Array,
    healthIndicatorGroups: Array,
    certifiers: Array,
    aiFillNonce: {
      type: Number,
      default: 0,
    },
    workspaceLayout: {
      type: String,
      default: 'classic',
    },
  },
  data() {
    return {
      activeTabs: {},
      removingRecordId: null,
      specialtyPanels: {
        [PANEL_ODONTOLOGY]: 'OdontologyPanel',
        [PANEL_OPHTHALMOLOGY]: 'OphthalmologyPanel',
      },
      addingAttachmentKey: null,
      pdfSource: '',
      pdfFileName: '',
      pdfSigned: false,
      printingMedrecId: null,
      expandedRecords: {},
    }
  },
  computed: {
    canDeleteRecord() {
      return permission.has('Delete Medical Record');
    },
    isClinical() {
      return this.workspaceLayout === 'clinical';
    },
  },
  mounted() {
    this.initializeActiveTabs();
  },
  watch: {
    // Observa apenas substituição do array (ex.: novo registro via getMedicalRecords).
    // Não usar deep + $forceUpdate: re-renderizava todos os inputs a cada salvamento
    // e resetava o cursor dos campos de texto em edição.
    medicalRecords: {
      handler(newRecords, oldRecords) {
        if (newRecords === oldRecords) {
          return;
        }

        this.$nextTick(() => {
          this.initializeActiveTabs();
        });
      },
    },
    workspaceLayout() {
      this.activeTabs = {};
      this.$nextTick(() => {
        this.initializeActiveTabs();
      });
    },
  },
  methods: {
    isRecordOpen(id, index) {
      if (Object.prototype.hasOwnProperty.call(this.expandedRecords, id)) {
        return this.expandedRecords[id];
      }

      return index === 0;
    },
    toggleRecord(id, index, event) {
      if (event?.target?.closest('.record-header-actions')) {
        return;
      }

      this.expandedRecords = {
        ...this.expandedRecords,
        [id]: !this.isRecordOpen(id, index),
      };
    },
    attendanceRecordTitle(medicalRecord) {
      return medicalRecordHeaderTitle(
        medicalRecord,
        this.schedule,
        (dateTime) => moment(dateTime).format('DD/MM/YYYY'),
      );
    },
    printMedicalRecord(medicalRecord) {
      this.printingMedrecId = medicalRecord.id_medrec;
      return this.$refs.medicalRecordPrinter.start(medicalRecord).finally(() => {
        this.printingMedrecId = null;
      });
    },
    onMedicalRecordPrinted(payload) {
      this.pdfSource = payload.pdfSource;
      this.pdfFileName = payload.pdfFileName;
      this.pdfSigned = payload.signed;
      this.$nextTick(() => showMedicalRecordPdf('modal_pdf_viewer_medical_record_attendance'));
    },
    removeMedicalRecord(medicalRecord) {
      const name = medicalRecord.record_type?.rectype_name || 'registro';

      Swal.fire({
        title: 'Remover registro?',
        html: `<b>${name}</b> sai deste atendimento. O que já foi salvo continua no histórico.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.removingRecordId = medicalRecord.id_medrec;
        http.delete(`/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}/medical-records/${medicalRecord.id_medrec}`)
          .then(() => {
            Alert.toastSuccess('Registro removido.');
            this.$emit('record-removed', medicalRecord);
          })
          .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
          .finally(() => { this.removingRecordId = null; });
      });
    },
    initializeActiveTabs() {
      if (this.medicalRecords && Array.isArray(this.medicalRecords) && this.medicalRecords.length > 0) {
        const newActiveTabs = { ...this.activeTabs };
        this.medicalRecords.forEach(record => {
          if (!newActiveTabs[record.id_medrec]) {
            newActiveTabs[record.id_medrec] = this.defaultTabForRecord(record);
          } else if (!this.isTabAllowed(record, newActiveTabs[record.id_medrec])) {
            newActiveTabs[record.id_medrec] = this.defaultTabForRecord(record);
          }
        });
        this.activeTabs = newActiveTabs;
      }
    },
    defaultTabForRecord(record) {
      return defaultSideTab(record, this.workspaceLayout);
    },
    isTabAllowed(record, tabName) {
      return isSideTabAllowed(record, tabName);
    },
    availableSideTabs(medicalRecord) {
      return listSideTabs(medicalRecord);
    },
    clinicalRecordTabs(medicalRecord) {
      return listClinicalTabs(medicalRecord);
    },
    isClinicalToolTab(medicalRecord) {
      const active = this.activeTabs[medicalRecord.id_medrec];
      return listSideTabs(medicalRecord).some((tab) => tab.id === active);
    },
    primarySideTabs(medicalRecord) {
      return this.availableSideTabs(medicalRecord).slice(0, 3);
    },
    overflowSideTabs(medicalRecord) {
      return this.availableSideTabs(medicalRecord).slice(3);
    },
    isOverflowTabActive(medicalRecord) {
      const active = this.activeTabs[medicalRecord.id_medrec];
      return this.overflowSideTabs(medicalRecord).some((tab) => tab.id === active);
    },
    isTabActive(medicalRecordId, tabName) {
      return this.activeTabs[medicalRecordId] === tabName;
    },
    setActiveTab(medicalRecordId, tabName) {
      this.activeTabs = Object.assign({}, this.activeTabs, {
        [medicalRecordId]: tabName
      });
    },
    saveAttribute(attribute) {
      let url = `/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}/medical-records/${attribute.id_medrec}/attributes/${attribute.id_medrec_attr}`;
      http.put(url, {'text': attribute.text, 'text_compl': attribute.text_compl}).then((_) => {
        const medicalRecord = this.medicalRecords.find((record) => record.id_medrec === attribute.id_medrec);
        const storedAttribute = medicalRecord?.attributes?.find((item) => item.id_medrec_attr === attribute.id_medrec_attr);

        if (storedAttribute) {
          // Não sobrescrever text: o Input mantém o valor local sincronizado enquanto o
          // profissional digita; atualizar aqui com o valor do debounce causaria perda de texto.
          storedAttribute.text_compl = attribute.text_compl;
        }
      }).catch((_) => {
        Alert.toastError("Erro ao salvar informação.");
      });
    },
    selectOverflowTab(event, medicalRecord, tab) {
      this.switchToTab('tab-' + tab.id + '-' + medicalRecord.id_medrec);
      this.closeOverflowDropdown(event);
    },
    closeOverflowDropdown(event) {
      const toggle = event?.currentTarget?.closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]');
      if (toggle && window.bootstrap?.Dropdown) {
        bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
      }

      document.querySelectorAll('.modern-dropdown-menu.show').forEach((menu) => {
        menu.classList.remove('show');
      });
    },
    switchToTab(tabId) {
      const medicalRecordId = tabId.split('-').pop();
      const tabName = tabId.replace(`tab-`, '').replace(`-${medicalRecordId}`, '');

      this.setActiveTab(medicalRecordId, tabName);

      this.$nextTick(() => {
        document.querySelectorAll(`[id*="${medicalRecordId}"]`).forEach(btn => {
          if (btn.classList.contains('nav-btn') || btn.classList.contains('nav-btn-mobile')) {
            btn.classList.remove('active');
          }
        });

        document.querySelectorAll(`[id*="${medicalRecordId}"]`).forEach(pane => {
          if (pane.classList.contains('tab-pane')) {
            pane.classList.remove('show', 'active');
          }
        });

        const targetPane = document.getElementById(tabId);
        if (targetPane) {
          targetPane.classList.add('show', 'active');
        }

        const desktopLink = document.getElementById(`nav-${tabName}-${medicalRecordId}`);
        const mobileLink = document.getElementById(`nav-mobile-${tabName}-${medicalRecordId}`);
        
        if (desktopLink) {
          desktopLink.classList.add('active');
        }
        if (mobileLink) {
          mobileLink.classList.add('active');
        }
      });
    },
    hasPreScheduleAnswer(medicalRecord) {
      return recordHasPreScheduleAnswer(medicalRecord);
    },
    attachmentActionKey(medicalRecord, question) {
      return `${medicalRecord.id_medrec}-${question.id_pre_schedule_question}`;
    },
    preScheduleFileId(medicalRecord, question) {
      const answers = medicalRecord?.schedule?.pre_schedule_answer?.answers;
      if (!answers) {
        return null;
      }

      const value = answers[question.id_pre_schedule_question] ?? answers[String(question.id_pre_schedule_question)];
      if (value && typeof value === 'object') {
        return value.id_file || null;
      }

      return null;
    },
    addFileToAttachments(medicalRecord, question) {
      if (!this.preScheduleFileId(medicalRecord, question)) {
        Alert.toastWarning('Este arquivo não está disponível para anexar à consulta.');
        return;
      }

      this.addingAttachmentKey = this.attachmentActionKey(medicalRecord, question);

      http.post(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${medicalRecord.id_medrec}/attachments/from-pre-schedule`, {
        question_id: question.id_pre_schedule_question,
      }).then(() => {
        Alert.toastSuccess('Arquivo adicionado aos anexos da consulta.');
        this.refreshAttachments(medicalRecord);
      }).catch((e) => {
        const message = typeof e.response?.data === 'string'
          ? e.response.data
          : e.response?.data?.message;
        Alert.toastError(message || 'Não foi possível adicionar o arquivo aos anexos.');
      }).finally(() => {
        this.addingAttachmentKey = null;
      });
    },
    refreshAttachments(medicalRecord) {
      const refs = this.$refs.attachmentRef;
      const list = Array.isArray(refs) ? refs : (refs ? [refs] : []);
      const target = list.find((component) => component?.medicalRecord?.id_medrec === medicalRecord.id_medrec);
      target?.getAttachments();
    },
    copyToAttribute(medicalRecord, question, formattedAnswer) {
      if (!formattedAnswer) {
        Alert.toastWarning('Não há resposta para copiar.');
        return;
      }

      const textToCopy = `${question.question_text}\n${formattedAnswer}`;

      // Função auxiliar para obter o valor atual do atributo do DOM
      const getCurrentAttributeText = (attributeId) => {
        // Tenta obter do textarea
        const textarea = document.getElementById(`attr-text-${attributeId}`);
        if (textarea) {
          return textarea.value ? textarea.value.trim() : '';
        }
        
        // Tenta obter do editor Quill (HTML)
        const quillEditor = document.querySelector(`#attr-html-${attributeId} .ql-editor`);
        if (quillEditor) {
          return quillEditor.innerHTML.trim();
        }
        
        // Fallback: usa o valor do objeto (pode estar desatualizado)
        const attr = medicalRecord.attributes.find(a => a.id_medrec_attr == attributeId);
        return attr && attr.text ? attr.text.trim() : '';
      };

      const targetAttributes = visibleAttributes(medicalRecord.attributes);
      if (targetAttributes.length === 0) {
        Alert.toastWarning('Este registro não tem campos para receber a resposta.');
        return;
      }

      // Se houver apenas um atributo, copiar diretamente
      if (targetAttributes.length === 1) {
        const attribute = targetAttributes[0];
        const existingText = getCurrentAttributeText(attribute.id_medrec_attr);
        const newText = existingText 
          ? `${existingText}\n\n---\n\n${textToCopy}` 
          : textToCopy;
        
        attribute.text = newText;
        this.saveAttribute(attribute);
        Alert.toastSuccess('Pergunta e resposta copiadas com sucesso!');
        return;
      }

      // Se houver múltiplos atributos, mostrar modal para seleção
      Swal.fire({
        title: 'Copiar para Atributo',
        text: 'Selecione o atributo para copiar a pergunta e resposta:',
        input: 'select',
        inputOptions: targetAttributes.reduce((options, attr) => {
          options[attr.id_medrec_attr] = attr.label;
          return options;
        }, {}),
        inputPlaceholder: 'Selecione um atributo',
        showCancelButton: true,
        confirmButtonText: 'Copiar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: 'btn btn-light'
        },
        inputValidator: (value) => {
          if (!value) {
            return 'Você precisa selecionar um atributo!';
          }
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          const attributeId = result.value;
          const attribute = targetAttributes.find(attr => attr.id_medrec_attr == attributeId);
          
          if (attribute) {
            // Obtém o valor atual do DOM ao invés do objeto em memória
            const existingText = getCurrentAttributeText(attributeId);
            const newText = existingText 
              ? `${existingText}\n\n---\n\n${textToCopy}` 
              : textToCopy;
            
            attribute.text = newText;
            this.saveAttribute(attribute);
            Alert.toastSuccess('Pergunta e resposta copiadas com sucesso!');
          }
        }
      });
    },
    specialtyPanelFor(medicalRecord) {
      return resolveSpecialtyPanel(this.schedule, medicalRecord, this.recordTypes);
    },
    bandAttributes(medicalRecord) {
      return splitFullWidthAttributes(medicalRecord?.attributes).band;
    },
    columnAttributes(medicalRecord) {
      return splitFullWidthAttributes(medicalRecord?.attributes).rest;
    },
  }
}
</script>

<style scoped>
/* Reset do Splitter */
::v-deep(.p-splitter) {
  border: none !important;
  box-shadow: none !important;
}

/* Card principal do registro médico */
.medical-record-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.medical-record-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

/* Header moderno do card */
.card-header-modern {
  background: #780f5a;
  border: none;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.card-header-modern:hover {
  background: #6a0d4e;
}

.card-header-modern.collapsed {
  background: #9a9a9a;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.record-type-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-type-icon .svg-icon {
  color: white;
}

.record-title {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  word-break: break-word;
}

.card-toolbar {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  transition: all 0.3s ease;
}

.card-toolbar:hover {
  background: rgba(255, 255, 255, 0.3);
}

.card-toolbar .svg-icon {
  color: white;
}

/* Card de conteúdo */
.content-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  transition: all 0.3s ease;
}

.content-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

/* Header da navegação moderna */
.modern-nav-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
}

/* Navegação desktop */
.nav-desktop-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-modern {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.nav-btn:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.15);
}

.nav-btn-primary {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-primary .nav-text {
  color: #6c757d;
}

.nav-btn-primary .nav-icon {
  color: #6c757d;
}

.nav-btn-primary:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-primary:hover .nav-text {
  color: #780f5a;
}

.nav-btn-primary:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-primary.active,
.nav-btn-primary.active:focus,
.nav-btn-primary.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-primary.active .nav-text {
  color: white !important;
}

.nav-btn-primary.active .nav-icon {
  color: white !important;
}

.nav-btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-secondary.active,
.nav-btn-secondary.active:focus,
.nav-btn-secondary.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-secondary.active .nav-text {
  color: white !important;
}

.nav-btn-secondary.active .nav-icon {
  color: white !important;
}

.nav-btn-secondary .nav-text {
  color: #6c757d;
}

.nav-btn-secondary .nav-icon {
  color: #6c757d;
}

.nav-btn-secondary:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-secondary:hover .nav-text {
  color: #780f5a;
}

.nav-btn-secondary:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-success {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-success.active,
.nav-btn-success.active:focus,
.nav-btn-success.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-success.active .nav-text {
  color: white !important;
}

.nav-btn-success.active .nav-icon {
  color: white !important;
}

.nav-btn-success .nav-text {
  color: #6c757d;
}

.nav-btn-success .nav-icon {
  color: #6c757d;
}

.nav-btn-success:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-success:hover .nav-text {
  color: #780f5a;
}

.nav-btn-success:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-info {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-info.active,
.nav-btn-info.active:focus,
.nav-btn-info.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-info.active .nav-text {
  color: white !important;
}

.nav-btn-info.active .nav-icon {
  color: white !important;
}

.nav-btn-info .nav-text {
  color: #6c757d;
}

.nav-btn-info .nav-icon {
  color: #6c757d;
}

.nav-btn-info:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-info:hover .nav-text {
  color: #780f5a;
}

.nav-btn-info:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-warning {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-warning.active,
.nav-btn-warning.active:focus,
.nav-btn-warning.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-warning.active .nav-text {
  color: white !important;
}

.nav-btn-warning.active .nav-icon {
  color: white !important;
}

.nav-btn-warning .nav-text {
  color: #6c757d;
}

.nav-btn-warning .nav-icon {
  color: #6c757d;
}

.nav-btn-warning:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-warning:hover .nav-text {
  color: #780f5a;
}

.nav-btn-warning:hover .nav-icon {
  color: #780f5a;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text {
  font-weight: 600;
}

/* Navegação mobile */
.nav-mobile {
  display: none;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-modern-mobile {
  display: flex;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  flex-wrap: wrap;
}

.nav-btn-mobile {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.nav-btn-mobile:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.15);
}

.nav-icon-mobile {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text-mobile {
  font-weight: 600;
}

/* Estados ativos para mobile */
.nav-btn-mobile.active {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-mobile.active .nav-text-mobile {
  color: white !important;
}

.nav-btn-mobile.active .nav-icon-mobile {
  color: white !important;
}

/* Dropdown moderno */
.modern-dropdown {
  position: relative;
}

.dropdown-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 7px 12px;
  color: #6c757d;
  transition: all 0.2s ease;
  cursor: pointer;
}

.dropdown-btn:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.15);
}

.dropdown-btn.is-current {
  background: #780f5a;
  border-color: #780f5a;
  color: white;
}

.modern-dropdown-item.active {
  background: rgba(120, 15, 90, 0.08);
  color: #780f5a;
}

.dropdown-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.modern-dropdown-menu {
  background: white;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  padding: 8px;
  margin-top: 8px;
  min-width: 180px;
}

.modern-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  background: none;
  border-radius: 8px;
  color: #495057;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.modern-dropdown-item:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #212529;
}

.dropdown-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.dropdown-item-text {
  font-weight: 600;
}

/* Card CID */
.cid-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  margin-top: 16px;
  transition: all 0.3s ease;
}

.cid-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

.cid-header {
  background: #780f5a;
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
}

.cid-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cid-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.cid-icon .svg-icon {
  color: white;
}

.cid-title-text {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cid-body {
  padding: 10px;
  overflow: visible !important;
  max-height: none !important;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
}

/* Garantir que não há scroll no CID */
.cid-card .card {
  overflow: visible !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cid-card .card-body {
  overflow: visible !important;
  max-height: none !important;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Responsividade */
@media (max-width: 1399.98px) {
  .nav-desktop-group {
    display: none !important;
  }
  .nav-mobile {
    display: flex !important;
  }
}

@media (min-width: 1400px) {
  .nav-desktop-group {
    display: flex !important;
  }
  .nav-mobile {
    display: none !important;
  }
  
  /* Garantir que as abas não quebrem linha */
  .nav-modern {
    flex-wrap: nowrap;
  }
}

.modal {
  z-index: 1055 !important;
}

.modal-backdrop {
  z-index: 1050 !important;
}

/* Animações */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.medical-record-card {
  animation: fadeInUp 0.6s ease-out;
}

.att-record-clinical-wrap {
  padding: 0 !important;
}

.att-record-clinical {
  display: flex;
  align-items: stretch;
  min-height: 520px;
}

.att-record-rail {
  width: 188px;
  flex-shrink: 0;
  padding: 0.75rem 0.55rem;
  background: #2b2d3a;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.att-record-rail-btn {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  border: 0;
  background: transparent;
  color: #c9cbd6;
  border-radius: 0.5rem;
  padding: 0.65rem 0.7rem;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: left;
}

.att-record-rail-btn i {
  font-size: 1rem;
  width: 1.1rem;
}

.att-record-rail-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.att-record-rail-btn.is-active {
  background: #780f5a;
  color: #fff;
}

.att-record-clinical-main {
  flex: 1;
  min-width: 0;
  padding: 1rem 1.15rem 1.25rem;
  background: #fff;
}

@media (max-width: 991px) {
  .att-record-clinical {
    flex-direction: column;
    min-height: 0;
  }

  .att-record-rail {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
  }

  .att-record-rail-btn {
    white-space: nowrap;
    width: auto;
  }
}

</style>
